import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { DATE_YMD, PROJECT_CARRIER_MAP_STATUS, RestService } from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { ContactsTable } from '../../cargo-chief-commons/contact/views/contacts-table';

@Component({
	selector: 'app-engage-carriers-dialog',
	templateUrl: './engage-carriers-dialog.component.html',
	styleUrls: ['./engage-carriers-dialog.component.scss'],
})
export class EngageCarriersDialogComponent implements OnInit, OnDestroy {
	showRateForm: boolean = true;
	showNotes: boolean = true;
	// CN-8661: expose the carrier rate's expiration date alongside the rate input.
	showExpirationDate: boolean = true;
	isFormSubmitted: boolean = false;
	submitButtonText: string = 'Engage';
	// CN-8312: Updated default dialog header
	dialogHeader: string = 'Log a call';

	carrierRespondedAt = new UntypedFormControl(new Date());
	// CN-8661: optional rate expiration; min today. Captured into the payload on submit.
	expirationDate = new UntypedFormControl();
	minExpirationDate = new Date();
	// CN-8312: contactName starts required (engage flow's "Log a call" UX). The
	// procure flows pass `contactNameOptional: true` via dialogData and
	// constructor below clears the validator so a user can procure without
	// filling anything in.
	rateDetailsForm: FormGroup = new FormGroup({
		contactName: new FormControl('', [Validators.required]),
		desiredPrice: new FormControl('', []),
		carrierRespondedAt: new FormControl('', []),
		status: new FormControl(PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS, []),
		notes: new FormControl('', []),
	});

	// CN-8312: typeahead state for "Who did you talk to?". Loads the carrier's
	// existing contacts and filters by firstName / lastName / emailAddress as
	// the user types. The input is still free-form: any typed value the user
	// doesn't pick from the list flows through unchanged.
	contacts: ContactsTable[] = [];
	filteredContacts: ContactsTable[] = [];
	// CN-8312: tracks the contact picked from the typeahead. Cleared whenever
	// the input value diverges from the picked contact's display name (so
	// type-after-select reverts to free-form). The dialog payload returns
	// this so the engage flow can persist the carrier+contact link on the
	// new feedback row that bumps Last Contact.
	selectedContact: ContactsTable | null = null;
	private isDestroyed$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<EngageCarriersDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: any,
		private restService: RestService,
		private urlService: UrlService
	) {
		this.showRateForm = this.dialogData.showRateForm;
		this.showNotes = this.dialogData.showNotes;
		// CN-8661: default on; callers can opt out with showExpirationDate: false.
		this.showExpirationDate = this.dialogData.showExpirationDate !== false;
		if (this.dialogData.expirationDate) {
			this.expirationDate = new UntypedFormControl(new Date(this.dialogData.expirationDate));
		}
		if (this.dialogData.submitButtonText) {
			this.submitButtonText = this.dialogData.submitButtonText;
		}
		if (this.dialogData.dialogHeader) {
			this.dialogHeader = this.dialogData.dialogHeader;
		}
		// CN-8312: Prevent dialog from being closed by clicking outside or pressing escape
		this.dialogRef.disableClose = true;
		// CN-8312: procure flows pass contactNameOptional=true so the user can
		// just click Procure with no input. Engage keeps the required validator.
		if (this.dialogData?.contactNameOptional) {
			this.rateDetailsForm.get('contactName')?.clearValidators();
			this.rateDetailsForm.get('contactName')?.updateValueAndValidity();
		}
		// CN-8312: pre-populate "Who did you talk to?" when the caller passes a
		// specific contact (e.g. clicking the phone/email/log-call icon on a
		// contact row in the V3 Carrier tray). Seed selectedContact so the submit
		// payload carries it, and set the field to the contact's display name.
		// The valueChanges guard in ngOnInit won't clear it because the seeded
		// value equals the picked contact's display name.
		if (this.dialogData?.selectedContact) {
			this.selectedContact = this.dialogData.selectedContact;
			this.rateDetailsForm
				.get('contactName')
				?.setValue(this.contactDisplayName(this.dialogData.selectedContact));
		}
	}

	ngOnInit() {
		// CN-8312: load carrier contacts for the typeahead. The dialog only opens
		// for a single carrier in both engage + remove flows (see callers in
		// recommended-carriers.effects.ts and project-carrier-map.effects.ts), so
		// carrierIds[0] is the right id.
		const carrierId =
			Array.isArray(this.dialogData?.carrierIds) && this.dialogData.carrierIds.length === 1
				? this.dialogData.carrierIds[0]
				: null;
		if (carrierId) {
			this.restService
				.GET(this.urlService.contactsUrl(carrierId))
				.pipe(takeUntil(this.isDestroyed$))
				.subscribe(
					(result) => {
						this.contacts = (result?.['data'] || []) as ContactsTable[];
						// CN-8312: if the user typed before the GET resolved, the
						// debounced valueChanges already fired against an empty
						// contacts list and produced an empty panel. Recompute now
						// against the loaded contacts so suggestions appear without
						// requiring another keystroke. Empty input still produces
						// an empty list (no auto-open).
						this.recomputeFilteredContacts(
							this.rateDetailsForm.get('contactName')?.value
						);
					},
					() => {
						this.contacts = [];
						this.filteredContacts = [];
					}
				);
		}
		this.rateDetailsForm
			.get('contactName')
			?.valueChanges.pipe(
				debounceTime(150),
				distinctUntilChanged(),
				takeUntil(this.isDestroyed$)
			)
			.subscribe((value) => {
				// CN-8312: clear the selectedContact whenever the typed value
				// no longer matches the picked contact's display name, so a
				// user who picks a contact and then edits the field doesn't
				// silently submit with a stale contact id.
				if (
					this.selectedContact &&
					typeof value === 'string' &&
					value !== this.contactDisplayName(this.selectedContact)
				) {
					this.selectedContact = null;
				}
				this.recomputeFilteredContacts(value);
			});
	}

	ngOnDestroy(): void {
		this.isDestroyed$.next();
		this.isDestroyed$.complete();
	}

	/**
	 * CN-8312: filter the loaded carrier contacts by case-insensitive
	 * "starts with" match against firstName, lastName, or emailAddress. An
	 * empty query produces an empty list so the mat-autocomplete panel
	 * doesn't open until the user actually starts typing — standard typeahead
	 * behavior. Prefix-match (not substring) because users type the beginning
	 * of the name they're looking for; substring matching produced noisy
	 * matches like "Tracy" when typing "r".
	 */
	private recomputeFilteredContacts(value: any): void {
		const query = (typeof value === 'string' ? value : '').trim().toLowerCase();
		if (!query) {
			this.filteredContacts = [];
			return;
		}
		this.filteredContacts = this.contacts
			.filter((c) => {
				const first = (c.firstName || '').toLowerCase();
				const last = (c.lastName || '').toLowerCase();
				const email = (c.emailAddress || '').toLowerCase();
				return first.startsWith(query) || last.startsWith(query) || email.startsWith(query);
			})
			.slice(0, 50);
	}

	/**
	 * Display function for mat-autocomplete. The form control stores a string,
	 * so this returns the value as-is when it's a string. When a contact is
	 * passed (the autocomplete library passes the option value here), formats
	 * the label using the same logic as the template.
	 */
	displayContact = (value: ContactsTable | string | null): string => {
		if (!value) return '';
		if (typeof value === 'string') return value;
		return this.contactDisplayName(value);
	};

	/**
	 * Best-effort display name for a contact: first/last name if set, else the
	 * precomputed fullName, else email. Used by the option template,
	 * onContactSelected, and the CN-8312 prefill path — fullName must be honored
	 * so a contact displayed from fullName (e.g. a Carrier-tray row) doesn't seed
	 * an empty contactName.
	 */
	contactDisplayName(contact: ContactsTable): string {
		const name = `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim();
		return name || contact?.fullName || contact?.emailAddress || '';
	}

	onContactSelected(contact: ContactsTable): void {
		this.selectedContact = contact;
		this.rateDetailsForm.get('contactName')?.setValue(this.contactDisplayName(contact));
	}

	submitForm(): void {
		// CN-8312: Only validate when the rate form is shown. The notes-only
		// "Add note and remove" flow has no required fields and must allow
		// submission with an empty form.
		if (this.showRateForm && this.rateDetailsForm.invalid) {
			this.rateDetailsForm.markAllAsTouched();
			return;
		}

		this.isFormSubmitted = true;
		this.rateDetailsForm.patchValue({
			carrierRespondedAt: DATE_YMD(this.carrierRespondedAt.value),
		});
		let payload = Object.assign({}, this.rateDetailsForm.value);
		// CN-8312: Material's mat-autocomplete sets the form value to the option's
		// [value] (the ContactsTable object) when the user picks from the typeahead.
		// Normalize back to a string so downstream string-interpolation (e.g. the
		// "Talked To: ${contactName}" note text) doesn't render "[object Object]".
		if (payload.contactName && typeof payload.contactName !== 'string') {
			payload.contactName = this.contactDisplayName(payload.contactName);
		}
		// CN-8312: Convert desiredPrice to cents (integer) if provided
		payload['carrierProvidingRate'] = false;
		if (payload.desiredPrice) {
			payload['desiredPrice'] = Math.round(Number(payload.desiredPrice.replace(/,/g, '')) * 100);
		}
		// CN-8661: forward the optional rate expiration date (YYYY-MM-DD) when set.
		if (this.expirationDate.value) {
			payload['expirationDate'] = DATE_YMD(this.expirationDate.value);
		}
		payload['carrierIDs'] = this.dialogData.carrierIds;
		// CN-8312: forward the selected contact (if any) so the engage effect
		// can attach contactId to the new feedback row written on success.
		// Free-form input leaves this null and feedback is recorded with no
		// contact reference.
		payload['selectedContact'] = this.selectedContact;
		if (this.dialogData.projectCarrierMapsList) {
			payload['projectCarrierMapsList'] = this.dialogData.projectCarrierMapsList;
		}
		this.closeDialog(payload);
	}

	closeDialog(data: any): void {
		this.dialogRef.close(data);
	}
}
